/**
 * Costs & Income, decided — OND-97.
 *
 * Pure. Registration fees, resale settlements, a period and a currency go in;
 * four totals, two charts, a table and a CSV come out. The copy is
 * `reporting.constants.ts`; the two reads and the period, currency and sort
 * state are `useReportingController.ts`.
 *
 * THIS FILE READS TWO LEDGERS AND WRITES NOTHING. `ondo.registration_fees` is
 * raised by `ondo.write_registration_fee()`, an AFTER INSERT trigger on
 * `ondo.transfers`, inside the transaction that recorded the sale;
 * `ondo.resale_settlements` is written by `ondo.write_resale_settlements()`
 * inside the resale's. CLAUDE.md's rule for this family is quoted in
 * `statement.workflow.ts` and reads across unchanged: a derived view may
 * describe charges that already exist and may not raise a fee, reprice one,
 * void one or mark one paid. There is no write path in this screen at all —
 * recording a fee as paid stays on the Transaction Centre, where the `admin`
 * rung that commits the firm's word already lives.
 *
 * NOTHING HERE COMPUTES A SETTLEMENT'S MONEY either. `galleryMinor` is frozen
 * onto the row by `ondo.settlement_arithmetic()`, for the reason
 * `packages/shared/src/domain/settlement.ts` states: "a figure recomputed on
 * read is a figure that can change under a party who already acted on it". The
 * only arithmetic in this file is addition of figures the database already
 * decided, and four rules govern it:
 *
 *   1. PER CURRENCY, ALWAYS. The ONDO tariff is in USD and a covenant is in
 *      whatever its first sale was written in, so the two sides of this screen
 *      are usually not in the same currency at all. Each currency is its own
 *      report and nothing is converted.
 *   2. A ROW AWAITING A RATE IS COUNTED AND NOT ADDED. Its figures are null
 *      until a director states a rate, and adding a null as zero would say a
 *      resale made nothing before anybody had said what it made.
 *   3. A SETTLEMENT OWING NOTHING IS STILL A SETTLEMENT. A loss, or a share
 *      that truncated to zero, is counted as a row and added to no total — the
 *      row is the record that the covenant was tested.
 *   4. A VOID ROW IS EXCLUDED ENTIRELY, never counted as zero. It was owed and
 *      then was not. It stays listable, behind a disclosure, because a
 *      withdrawn sale is a fact a director may need to inspect.
 *
 * ONE DATE BASIS PER SIDE, AND THE STATUS DECIDES THE TOTAL. A fee enters a
 * period by `createdAt` — the instant the sale raised it — and a settlement by
 * `resaleSoldAt`. Whether it lands in the outstanding total or the recorded one
 * is then a question about its status and not about a second date.
 *
 * REJECTED: dating each MEASURE by its own event — a fee by `createdAt` when
 * owed and by `paidAt` once paid, a share by the resale when owed and by
 * `collectedAt` once received. It is the more usual accounting shape and it
 * breaks the one thing OND-97 asks for in terms: "do not silently use different
 * date meanings between a card and its graph". Under it a fee raised in January
 * and paid in March is in two periods, the four totals stop reconciling, and no
 * single sentence can tell a reader what "March" means. Under the basis above,
 * outstanding plus recorded is exactly what the period raised, every record is
 * in one period and one total, and the table always adds up to the tiles.
 *
 * AND THE BOUNDARIES ARE UTC. The instants on both tables are UTC and a
 * gallery's local midnight would move a row between months according to where
 * the reader was sitting, so two directors would disagree about a month. The
 * screen says so rather than leaving it to be discovered.
 */

import type { RegistrationFee, Settlement } from "@ondo/shared";
import { percentOfBp, settlementState, toCsv } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatDay } from "../lib/dates.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { money as sharedMoney } from "../lib/money.ts";
import { REPORTING_COPY, REPORTING_PERIOD_LABELS } from "./reporting.constants.ts";
import type { ReportingPeriodKey, ReportingSortKey } from "./reporting.constants.ts";

/**
 * Every figure on this screen is formatted the same way, which is a choice.
 *
 * `transactions.model.ts` prints fees in en-US to match the Sell wizard's
 * preview of the same tariff; the settlement screens use the en-GB default. A
 * screen whose whole subject is the two side by side cannot do both — one
 * currency reading two ways down one column is worse than either convention —
 * so this screen uses the registry default throughout, and `US$350` here is the
 * same fee as `$350` there.
 */
function money(minor: number | null, currency: string): string {
  return sharedMoney(minor, currency, "—");
}

// ───────────────────────────────────────────────────────── the period ──

/** A resolved reporting period. Half-open: `startIso` inclusive, `endIso` not. */
export interface ResolvedPeriod {
  key: ReportingPeriodKey;
  /** 'This month', 'Custom range'. */
  label: string;
  /** Null for `all-time`, which is the absence of a bound rather than a wide one. */
  startIso: string | null;
  endIso: string | null;
  /** '01 Mar 2026 — 31 Mar 2026', or the all-time sentence. */
  rangeLabel: string;
  /** What is wrong with the typed range, or null. Only ever set for `custom`. */
  problem: string | null;
}

/** 'YYYY-MM-DD', as the two custom-range inputs hand it over. */
export interface CustomRange {
  from: string;
  to: string;
}

function utcDay(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month, day)).toISOString();
}

/**
 * The period, resolved against one clock.
 *
 * `now` is a parameter rather than a call inside — `toSettlementsView`'s rule —
 * so a test can sit on a quarter boundary without waiting for one.
 *
 * `Date.UTC` is given out-of-range months deliberately: month 12 is January of
 * the next year and month -1 is the previous December, which is how
 * `last-month` and `last-quarter` roll over a year end without a branch.
 */
export function resolvePeriod(
  key: ReportingPeriodKey,
  now: Date,
  custom?: CustomRange,
): ResolvedPeriod {
  const label = REPORTING_PERIOD_LABELS[key];
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const quarter = Math.floor(month / 3) * 3;

  const bounded = (startIso: string, endIso: string): ResolvedPeriod => ({
    key,
    label,
    startIso,
    endIso,
    rangeLabel: REPORTING_COPY.period.range(
      formatDay(startIso, { utc: true }),
      // The LAST instant inside the period, so a reader of "1 — 31 March" is
      // not told the period ends on 1 April. The boundary stays half-open
      // everywhere it is compared; only the sentence is inclusive.
      formatDay(new Date(Date.parse(endIso) - 1).toISOString(), { utc: true }),
    ),
    problem: null,
  });

  switch (key) {
    case "this-month":
      return bounded(utcDay(year, month, 1), utcDay(year, month + 1, 1));
    case "last-month":
      return bounded(utcDay(year, month - 1, 1), utcDay(year, month, 1));
    case "this-quarter":
      return bounded(utcDay(year, quarter, 1), utcDay(year, quarter + 3, 1));
    case "last-quarter":
      return bounded(utcDay(year, quarter - 3, 1), utcDay(year, quarter, 1));
    case "this-year":
      return bounded(utcDay(year, 0, 1), utcDay(year + 1, 0, 1));
    case "last-year":
      return bounded(utcDay(year - 1, 0, 1), utcDay(year, 0, 1));
    case "all-time":
      return {
        key,
        label,
        startIso: null,
        endIso: null,
        rangeLabel: REPORTING_COPY.period.everything,
        problem: null,
      };
    case "custom":
      return resolveCustom(label, custom);
  }
}

/**
 * The typed range, or the reason it cannot be used.
 *
 * THE END DATE IS INCLUSIVE AS TYPED and half-open underneath: somebody who
 * types 1 to 31 March means all of 31 March, so the bound becomes the start of
 * 1 April. Getting this wrong is the classic off-by-a-day that loses a month's
 * last sale, which is why it is done once here rather than at each comparison.
 *
 * A HALF-TYPED RANGE IS NOT AN ERROR, and it is not a period either. It reports
 * `problem` and no bounds, so the screen can say what is missing while the
 * reader is still typing rather than flashing a result for a range they did not
 * ask for.
 */
function resolveCustom(label: string, custom?: CustomRange): ResolvedPeriod {
  const blank = (problem: string): ResolvedPeriod => ({
    key: "custom",
    label,
    startIso: null,
    endIso: null,
    rangeLabel: "",
    problem,
  });

  const from = custom?.from ?? "";
  const to = custom?.to ?? "";
  if (!from || !to) return blank(REPORTING_COPY.period.customIncomplete);

  const start = Date.parse(`${from}T00:00:00.000Z`);
  const last = Date.parse(`${to}T00:00:00.000Z`);
  if (Number.isNaN(start) || Number.isNaN(last)) {
    return blank(REPORTING_COPY.period.customInvalid);
  }
  if (start > last) return blank(REPORTING_COPY.period.customInvalid);

  const endIso = new Date(last + DAY_MS).toISOString();
  const startIso = new Date(start).toISOString();

  return {
    key: "custom",
    label,
    startIso,
    endIso,
    rangeLabel: REPORTING_COPY.period.range(
      formatDay(startIso, { utc: true }),
      formatDay(new Date(last).toISOString(), { utc: true }),
    ),
    problem: null,
  };
}

const DAY_MS = 86_400_000;

/** Whether an instant falls inside a resolved period. Half-open, both bounds optional. */
export function withinPeriod(iso: string, period: ResolvedPeriod): boolean {
  const at = Date.parse(iso);
  // A row whose date cannot be parsed is kept rather than silently dropped: a
  // ledger outlives the build that wrote it, and a malformed instant must not
  // delete money from a total. It sorts last and is visible in the table.
  if (Number.isNaN(at)) return true;
  if (period.startIso && at < Date.parse(period.startIso)) return false;

  return !(period.endIso && at >= Date.parse(period.endIso));
}

// ───────────────────────────────────────────────────────────── the rows ──

export type ReportingSide = "fee" | "share";

/**
 * What a record is doing, across both ledgers.
 *
 * FIVE, not two, and the three beyond outstanding/recorded are the ones that
 * keep a total honest. `pending` has no figure yet, `nothing_owed` has a figure
 * of zero that was genuinely nothing, and `void` stopped being owed.
 */
export type ReportingStatus = "due" | "recorded" | "pending" | "nothing_owed" | "void";

/** One fee or one settlement, with every label already decided. */
export interface ReportingRow {
  key: string;
  workId: string;
  side: ReportingSide;
  /** 'ONDO fee' / 'Resale share'. */
  sideLabel: string;
  /** 'Registration fee · premium, covenant attached'. */
  kindLabel: string;
  title: string;
  ondoCode: string;
  attributedName: string;
  /** The instant the row is dated by. Sorting and bucketing both read this. */
  dateIso: string;
  /** '14 Mar 2026'. */
  date: string;
  status: ReportingStatus;
  statusLabel: string;
  /** Null when there is no figure — a row awaiting a rate, and only that. */
  amountMinor: number | null;
  /** '£172,000', or an em dash. */
  amount: string;
  currency: string;
  href: string;
}

/** Every fee as a row, dated by the sale that raised it. */
export function feeRows(fees: RegistrationFee[], workBase: string): ReportingRow[] {
  return fees.map((fee) => ({
    key: `fee-${fee.feeId}`,
    workId: fee.workId,
    side: "fee" as const,
    sideLabel: REPORTING_COPY.sides.fee,
    kindLabel: REPORTING_COPY.feeKind[fee.tier],
    title: fee.title,
    ondoCode: fee.ondoCode,
    attributedName: fee.attributedName,
    dateIso: fee.createdAt,
    date: formatDay(fee.createdAt, { utc: true, fallback: "—" }),
    status: feeStatus(fee),
    statusLabel: REPORTING_COPY.status[feeStatus(fee)],
    amountMinor: fee.amountMinor,
    amount: money(fee.amountMinor, fee.currency),
    currency: fee.currency,
    href: `${workBase}/${fee.workId}`,
  }));
}

/**
 * A fee is outstanding, recorded as paid, or void.
 *
 * It has no `pending` and no `nothing_owed`: the tariff is a fixed figure in
 * one currency, written with the sale, so a fee always has an amount and is
 * never zero. `registration_fees_not_paid_and_void` makes paid and void
 * mutually exclusive in Postgres, and void is read first for the reason
 * `settlementState` reads it first — a withdrawn sale ends the obligation
 * whatever else was recorded against it.
 */
export function feeStatus(fee: RegistrationFee): ReportingStatus {
  if (fee.voidedAt !== null) return "void";

  return fee.paidAt !== null ? "recorded" : "due";
}

/**
 * Every settlement as a row, dated by the resale and carrying THIS GALLERY'S
 * OWN SHARE.
 *
 * `galleryMinor`, never `shareMinor`. The ticket says so — "use only the
 * gallery's own resale share, not the combined gallery-and-artist amount" — and
 * the reason is that `shareMinor` includes the artist's half, which this gallery
 * collects and passes straight on. A report that counted it as income would
 * overstate a 50/50 covenant by exactly double.
 */
export function shareRows(settlements: Settlement[], workBase: string): ReportingRow[] {
  return settlements.map((each) => {
    const status = shareStatus(each);

    return {
      key: `share-${each.settlementId}`,
      workId: each.workId,
      side: "share" as const,
      sideLabel: REPORTING_COPY.sides.share,
      kindLabel: REPORTING_COPY.shareKind(
        percentOfBp(each.shareBp),
        percentOfBp(each.gallerySplitBp),
      ),
      title: each.title,
      ondoCode: each.ondoCode,
      attributedName: each.attributedName,
      dateIso: each.resaleSoldAt,
      date: formatDay(each.resaleSoldAt, { utc: true, fallback: "—" }),
      status,
      statusLabel: REPORTING_COPY.status[status],
      amountMinor: each.galleryMinor,
      amount: money(each.galleryMinor, each.currency),
      currency: each.currency,
      href: `${workBase}/${each.workId}`,
    };
  });
}

/**
 * The settlement's own state, read as this report's five.
 *
 * `settlementState` is the authority and is not re-derived here — two copies of
 * a rule about six timestamps is exactly the pair that drifts. 'collected' and
 * 'settled' both mean the gallery has recorded receiving its share: the
 * difference between them is whether the ARTIST has been paid on, which is the
 * settlements screen's business and not this report's.
 */
export function shareStatus(settlement: Settlement): ReportingStatus {
  switch (settlementState(settlement)) {
    case "void":
      return "void";
    case "awaiting_rate":
      return "pending";
    case "nothing_owed":
      return "nothing_owed";
    case "collected":
    case "settled":
      return "recorded";
    case "due":
      return "due";
  }
}

// ──────────────────────────────────────────────────── the four totals ──

/** What one currency's two ledgers come to over the period. Minor units. */
export interface CurrencyPosition {
  currency: string;
  feesDueMinor: number;
  feesPaidMinor: number;
  sharesDueMinor: number;
  sharesReceivedMinor: number;
  /** Fees counted into one of the two fee figures. */
  fees: number;
  feesVoided: number;
  /** Settlements counted into one of the two share figures. */
  shares: number;
  /** Counted and added to nothing: no rate has been stated. */
  sharesPending: number;
  /** Counted and added to nothing: the resale made no profit to share. */
  sharesNothingOwed: number;
  sharesVoided: number;
  /** Every row in this currency, void included. Drives the currency chooser. */
  records: number;
}

/**
 * The position in each currency, in code order.
 *
 * Sorted by currency code so two readers with the same ledgers see the same
 * sections and a currency never moves when a fee lands — the rule
 * `totalsByCurrency` keeps for the same reason.
 */
export function positionsByCurrency(rows: ReportingRow[]): CurrencyPosition[] {
  const byCurrency = new Map<string, CurrencyPosition>();

  for (const row of rows) {
    const position =
      byCurrency.get(row.currency) ??
      ({
        currency: row.currency,
        feesDueMinor: 0,
        feesPaidMinor: 0,
        sharesDueMinor: 0,
        sharesReceivedMinor: 0,
        fees: 0,
        feesVoided: 0,
        shares: 0,
        sharesPending: 0,
        sharesNothingOwed: 0,
        sharesVoided: 0,
        records: 0,
      } satisfies CurrencyPosition);

    position.records += 1;
    addRow(position, row);
    byCurrency.set(row.currency, position);
  }

  return [...byCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * One row into one position, and the four money rules are all here.
 *
 * `?? 0` appears twice and never widens a total, because both arms are reached
 * only on a status whose figure is non-null — a null amount is `pending`, which
 * is counted above and returns before either. The coalesce is there so the type
 * is honest rather than asserted.
 */
function addRow(position: CurrencyPosition, row: ReportingRow): void {
  if (row.status === "void") {
    // Rule 4: excluded from every figure AND from the counted rows. It was owed
    // and then was not, so it is a fact about a withdrawn sale rather than a
    // line in this report.
    if (row.side === "fee") position.feesVoided += 1;
    else position.sharesVoided += 1;
    return;
  }

  if (row.side === "fee") {
    position.fees += 1;
    if (row.status === "recorded") position.feesPaidMinor += row.amountMinor ?? 0;
    else position.feesDueMinor += row.amountMinor ?? 0;
    return;
  }

  position.shares += 1;
  switch (row.status) {
    // Rule 2: counted, never added. There is no figure to add yet.
    case "pending":
      position.sharesPending += 1;
      return;
    // Rule 3: a settlement that owes nothing is still a settlement, and zero is
    // not a sum.
    case "nothing_owed":
      position.sharesNothingOwed += 1;
      return;
    case "recorded":
      position.sharesReceivedMinor += row.amountMinor ?? 0;
      return;
    default:
      position.sharesDueMinor += row.amountMinor ?? 0;
  }
}

/**
 * The four tiles, in the order the ticket names them: what we owe, what we have
 * recorded paying, what we are owed, what we have recorded receiving.
 *
 * Cost first and income second, both in that order, so a reader scanning the
 * row never finds an outstanding figure where a recorded one was the last time.
 * Every note says what the figure is made of, and the three kinds of row that
 * are counted without being added each name themselves there rather than
 * disappearing into it.
 */
export function describePosition(position: CurrencyPosition): Tile[] {
  const { currency } = position;
  const feeNote = [REPORTING_COPY.totals.fees(position.fees)];
  if (position.feesVoided > 0) {
    feeNote.push(REPORTING_COPY.totals.voided(position.feesVoided));
  }

  const shareNote = [REPORTING_COPY.totals.shares(position.shares)];
  if (position.sharesPending > 0) {
    shareNote.push(REPORTING_COPY.totals.pending(position.sharesPending));
  }
  if (position.sharesNothingOwed > 0) {
    shareNote.push(REPORTING_COPY.totals.nothingOwed(position.sharesNothingOwed));
  }
  if (position.sharesVoided > 0) {
    shareNote.push(REPORTING_COPY.totals.voided(position.sharesVoided));
  }

  // The currency is the GROUP's heading rather than four repetitions inside
  // it — see `REPORTING_COPY.totals.heading`. Each figure still carries its own
  // currency symbol, so a tile read on its own is never ambiguous.
  return [
    {
      key: `fees-due-${currency}`,
      label: REPORTING_COPY.totals.feesDue,
      value: money(position.feesDueMinor, currency),
      note: feeNote.join(" · "),
    },
    {
      key: `fees-paid-${currency}`,
      label: REPORTING_COPY.totals.feesPaid,
      value: money(position.feesPaidMinor, currency),
      note: null,
    },
    {
      key: `shares-due-${currency}`,
      label: REPORTING_COPY.totals.sharesDue,
      value: money(position.sharesDueMinor, currency),
      note: shareNote.join(" · "),
    },
    {
      key: `shares-received-${currency}`,
      label: REPORTING_COPY.totals.sharesReceived,
      value: money(position.sharesReceivedMinor, currency),
      note: null,
    },
  ];
}

/**
 * The net, said only when it can be.
 *
 * A NET NEEDS BOTH LEDGERS IN ONE CURRENCY, and the ordinary case is that it
 * does not have them: the ONDO tariff is USD and a covenant carries whatever
 * its first sale was written in. So this returns the one-sided sentence rather
 * than a figure, because the alternative is a conversion nobody made — which
 * OND-97 forbids in terms and this whole screen is built to avoid.
 *
 * It nets RECORDED against RECORDED. Netting what is owed against what is owed
 * would be a cash position nobody holds: an outstanding fee is money the firm
 * has not spent and an outstanding share is money it has not got.
 */
export function describeNet(position: CurrencyPosition): string {
  const { currency } = position;
  const hasFees = position.fees > 0;
  const hasShares = position.shares > 0;

  if (!hasFees || !hasShares) return REPORTING_COPY.net.oneSided(currency);

  const net = position.sharesReceivedMinor - position.feesPaidMinor;

  return REPORTING_COPY.net.recorded(
    currency,
    money(net, currency),
    money(position.sharesReceivedMinor, currency),
    money(position.feesPaidMinor, currency),
  );
}

// ────────────────────────────────────────────────────────── the charts ──

/** Which of the two ledgers a mark belongs to. Never a status colour. */
export type ReportingSeries = "cost" | "income";
/** Outstanding is hatched, recorded is solid — so colour is never the only cue. */
export type ReportingTone = "due" | "recorded";

/** One bar of the at-a-glance comparison. */
export interface ComparisonBar {
  key: string;
  label: string;
  /** '£172,000'. */
  value: string;
  /** Of the largest bar on the chart, 0–1. Geometry the view only multiplies. */
  fraction: number;
  series: ReportingSeries;
  tone: ReportingTone;
}

/**
 * The four measures as one scale.
 *
 * ONE AXIS, which is the whole reason this is a single chart. Two y-scales — a
 * fee tariff of a few hundred against a settlement of a few hundred thousand —
 * would let a reader compare two heights that are not comparable, and that is
 * the single most misleading thing a chart of this data could do. Where the
 * figures really are orders of magnitude apart the fee bars are genuinely
 * tiny, which is the truth about the comparison and is why the figure is
 * printed beside every bar.
 */
export function describeComparison(position: CurrencyPosition): ComparisonBar[] {
  const bars: Omit<ComparisonBar, "fraction">[] = [
    {
      key: "fees-due",
      label: REPORTING_COPY.charts.legend.feesDue,
      value: money(position.feesDueMinor, position.currency),
      series: "cost",
      tone: "due",
    },
    {
      key: "fees-paid",
      label: REPORTING_COPY.charts.legend.feesPaid,
      value: money(position.feesPaidMinor, position.currency),
      series: "cost",
      tone: "recorded",
    },
    {
      key: "shares-due",
      label: REPORTING_COPY.charts.legend.sharesDue,
      value: money(position.sharesDueMinor, position.currency),
      series: "income",
      tone: "due",
    },
    {
      key: "shares-received",
      label: REPORTING_COPY.charts.legend.sharesReceived,
      value: money(position.sharesReceivedMinor, position.currency),
      series: "income",
      tone: "recorded",
    },
  ];

  const minors = [
    position.feesDueMinor,
    position.feesPaidMinor,
    position.sharesDueMinor,
    position.sharesReceivedMinor,
  ];
  const max = Math.max(...minors);

  return bars.map((bar, index) => ({
    ...bar,
    // An all-zero period draws four empty bars rather than four full ones,
    // which is what dividing by a zero max would do.
    fraction: max > 0 ? (minors[index] ?? 0) / max : 0,
  }));
}

/** One stacked segment of a bucket's column. */
export interface BucketSegment {
  key: string;
  tone: ReportingTone;
  /** Of the tallest column on the chart, 0–1. */
  fraction: number;
  /** '£172,000'. The segment's own figure, for its tooltip. */
  value: string;
  label: string;
}

/** One side's column inside one bucket. */
export interface BucketColumn {
  key: string;
  series: ReportingSeries;
  label: string;
  /** '£344,000' — outstanding and recorded together, which is what the period raised. */
  total: string;
  segments: BucketSegment[];
}

/** One day or one month on the time chart. */
export interface TimeBucket {
  key: string;
  /** '14 Mar' or 'Mar 2026'. */
  label: string;
  columns: BucketColumn[];
  /** True when nothing at all was recorded here. Drawn anyway, so the axis is continuous. */
  empty: boolean;
}

/** Day buckets up to this span, month buckets beyond it. */
const DAILY_SPAN_DAYS = 62;

/**
 * The period, bucketed.
 *
 * DAY OR MONTH, decided by the span rather than by the preset, so a custom
 * fortnight gets days and a custom decade gets months without a special case.
 * Two months is the threshold: sixty-two daily columns is about as many as the
 * chart can carry, and a quarter of them would be unreadable.
 *
 * EMPTY BUCKETS ARE DRAWN. A month with no sale is a fact about the period, and
 * a chart that closed the gap would put February beside April and invite a
 * reader to see a trend across a month that never happened.
 *
 * For `all-time` the axis runs from the earliest record to the latest, because
 * there is no period to run it from and starting at the epoch would draw
 * decades of nothing.
 */
export function bucketsFor(
  rows: ReportingRow[],
  period: ResolvedPeriod,
  currency: string,
): TimeBucket[] {
  const stamps = rows
    .map((row) => Date.parse(row.dateIso))
    .filter((at) => !Number.isNaN(at));
  if (stamps.length === 0) return [];

  const from = period.startIso ? Date.parse(period.startIso) : Math.min(...stamps);
  const to = period.endIso ? Date.parse(period.endIso) : Math.max(...stamps) + 1;
  const monthly = to - from > DAILY_SPAN_DAYS * DAY_MS;

  const keys = monthly ? monthKeys(from, to) : dayKeys(from, to);
  const totals = new Map<string, CurrencyPosition>();
  for (const key of keys) totals.set(key, emptyPosition(currency));

  for (const row of rows) {
    const at = Date.parse(row.dateIso);
    if (Number.isNaN(at)) continue;
    const key = monthly ? monthKey(at) : dayKey(at);
    const position = totals.get(key);
    // A row outside the plotted axis is left out of the chart and stays in the
    // table. It can only happen on a date the period itself does not cover,
    // which `withinPeriod` has already excluded, or an unparseable one.
    if (position) addRow(position, row);
  }

  const max = Math.max(
    ...[...totals.values()].map(
      (each) =>
        Math.max(
          each.feesDueMinor + each.feesPaidMinor,
          each.sharesDueMinor + each.sharesReceivedMinor,
        ),
    ),
    0,
  );

  return keys.map((key) => {
    const position = totals.get(key) ?? emptyPosition(currency);
    const empty =
      position.feesDueMinor === 0 &&
      position.feesPaidMinor === 0 &&
      position.sharesDueMinor === 0 &&
      position.sharesReceivedMinor === 0;

    return {
      key,
      label: monthly ? monthLabel(key) : dayLabel(key),
      empty,
      columns: [
        column({
          bucketKey: key,
          series: "cost",
          currency,
          max,
          recordedMinor: position.feesPaidMinor,
          dueMinor: position.feesDueMinor,
          recordedLabel: REPORTING_COPY.charts.legend.feesPaid,
          dueLabel: REPORTING_COPY.charts.legend.feesDue,
        }),
        column({
          bucketKey: key,
          series: "income",
          currency,
          max,
          recordedMinor: position.sharesReceivedMinor,
          dueMinor: position.sharesDueMinor,
          recordedLabel: REPORTING_COPY.charts.legend.sharesReceived,
          dueLabel: REPORTING_COPY.charts.legend.sharesDue,
        }),
      ],
    };
  });
}

/**
 * One column, recorded at the bottom and outstanding above it.
 *
 * STACKED, and the stack is meaningful rather than decorative: the two
 * segments are the whole of what the bucket raised, split by whether it has
 * been recorded as moved. They cannot double-count, because every record is in
 * exactly one of them. Recorded sits at the baseline because it is the settled
 * part of the figure and the part a reader trusts first.
 */
function column(input: {
  bucketKey: string;
  series: ReportingSeries;
  currency: string;
  max: number;
  recordedMinor: number;
  dueMinor: number;
  recordedLabel: string;
  dueLabel: string;
}): BucketColumn {
  const { bucketKey, series, currency, max, recordedMinor, dueMinor } = input;
  const fraction = (minor: number) => (max > 0 ? minor / max : 0);

  return {
    key: `${bucketKey}-${series}`,
    series,
    label: series === "cost" ? REPORTING_COPY.sides.fee : REPORTING_COPY.sides.share,
    total: money(recordedMinor + dueMinor, currency),
    segments: [
      {
        key: `${bucketKey}-${series}-recorded`,
        tone: "recorded",
        fraction: fraction(recordedMinor),
        value: money(recordedMinor, currency),
        label: input.recordedLabel,
      },
      {
        key: `${bucketKey}-${series}-due`,
        tone: "due",
        fraction: fraction(dueMinor),
        value: money(dueMinor, currency),
        label: input.dueLabel,
      },
    ],
  };
}

function emptyPosition(currency: string): CurrencyPosition {
  return {
    currency,
    feesDueMinor: 0,
    feesPaidMinor: 0,
    sharesDueMinor: 0,
    sharesReceivedMinor: 0,
    fees: 0,
    feesVoided: 0,
    shares: 0,
    sharesPending: 0,
    sharesNothingOwed: 0,
    sharesVoided: 0,
    records: 0,
  };
}

function dayKey(at: number): string {
  return new Date(at).toISOString().slice(0, 10);
}

function monthKey(at: number): string {
  return new Date(at).toISOString().slice(0, 7);
}

function dayKeys(from: number, to: number): string[] {
  const keys: string[] = [];
  const start = Date.parse(`${dayKey(from)}T00:00:00.000Z`);
  for (let at = start; at < to; at += DAY_MS) keys.push(dayKey(at));

  return keys;
}

function monthKeys(from: number, to: number): string[] {
  const keys: string[] = [];
  const first = new Date(from);
  let year = first.getUTCFullYear();
  let month = first.getUTCMonth();

  while (Date.UTC(year, month, 1) < to) {
    keys.push(monthKey(Date.UTC(year, month, 1)));
    month += 1;
    if (month === 12) {
      month = 0;
      year += 1;
    }
  }

  return keys;
}

/**
 * '04 Feb' and 'Feb 26' — short, because these are axis ticks.
 *
 * Deliberately NOT `formatDay`, which is the registry's row format and spells
 * the year out: twelve '01 Jan 2026's in a row collide and the last one is
 * clipped, which is what a year of monthly buckets drew before this. The year
 * stays on the month tick, in two digits, so a range spanning a year boundary
 * is still unambiguous; the day tick drops it, because a daily axis is never
 * more than a couple of months long and the period is named above the chart.
 */
function tickLabel(at: string, options: Intl.DateTimeFormatOptions, fallback: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" }).format(
      new Date(at),
    );
  } catch {
    return fallback;
  }
}

function dayLabel(key: string): string {
  return tickLabel(`${key}T00:00:00.000Z`, { day: "2-digit", month: "short" }, key);
}

function monthLabel(key: string): string {
  return tickLabel(`${key}-01T00:00:00.000Z`, { month: "short", year: "2-digit" }, key);
}

// ─────────────────────────────────────────────────────────── the table ──

/**
 * The rows, ordered.
 *
 * Newest first by default, which is what both ledgers already answer in. A row
 * with no figure sorts as if it had none rather than as if it had zero — it
 * goes last on an amount sort, because a settlement awaiting a rate is not the
 * cheapest thing in the period.
 */
export function sortRows(rows: ReportingRow[], sort: ReportingSortKey): ReportingRow[] {
  const ordered = [...rows];

  switch (sort) {
    case "amount":
      return ordered.sort(
        (a, b) =>
          (b.amountMinor ?? -1) - (a.amountMinor ?? -1) || a.title.localeCompare(b.title),
      );
    case "work":
      return ordered.sort(
        (a, b) => a.title.localeCompare(b.title) || b.dateIso.localeCompare(a.dateIso),
      );
    case "side":
      return ordered.sort(
        (a, b) => a.side.localeCompare(b.side) || b.dateIso.localeCompare(a.dateIso),
      );
    default:
      return ordered.sort(
        (a, b) => b.dateIso.localeCompare(a.dateIso) || a.title.localeCompare(b.title),
      );
  }
}

// ───────────────────────────────────────────────────────────── the view ──

/** One currency the reader can switch to, and how much is in it. */
export interface CurrencyChoice {
  currency: string;
  label: string;
  records: number;
}

export type ReportingView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | {
      kind: "ready";
      period: ResolvedPeriod;
      /** Every currency across both ledgers, whole-history, so the chooser never empties. */
      currencies: CurrencyChoice[];
      currency: string | null;
      /** The four tiles. Empty only when there is no currency at all. */
      totals: Tile[];
      netLine: string;
      comparison: ComparisonBar[];
      comparisonLabel: string;
      buckets: TimeBucket[];
      timeLabel: string;
      grouping: string;
      /** Everything counted, ordered. */
      rows: ReportingRow[];
      /** Excluded from every total, listed behind a disclosure. */
      voidRows: ReportingRow[];
      /** Said instead of a table when the period holds nothing. */
      emptyNote: string | null;
      position: CurrencyPosition | null;
    };

/**
 * Everything the screen renders, decided here.
 *
 * THERE IS NO `empty` KIND, and that is deliberate. An empty period is the one
 * state a reader most needs the controls for — they have just asked about March
 * and want to ask about February — and a kind that replaced the screen with a
 * sentence would take the period chips away with it. So an empty period is
 * `ready` with a note where the table goes.
 *
 * THE CURRENCY CHOOSER IS BUILT FROM THE WHOLE HISTORY, not from the period.
 * A reader who narrows to a month in which only USD moved must still be able to
 * switch back to GBP; a chooser rebuilt from the period would delete the option
 * that would have shown them something.
 */
export function toReportingView(input: {
  fees: RegistrationFee[] | null;
  settlements: Settlement[] | null;
  error: string | null;
  workBase: string;
  periodKey: ReportingPeriodKey;
  // Explicitly `| undefined` rather than bare optional, because
  // `exactOptionalPropertyTypes` is on: a caller that always passes the key and
  // sometimes passes nothing in it is the ordinary case here, and this is the
  // only shape that admits both.
  custom?: CustomRange | undefined;
  currency: string | null;
  sort: ReportingSortKey;
  now: Date;
}): ReportingView {
  const { fees, settlements, error, workBase, currency, sort, now } = input;

  if (error) return { kind: "error", title: REPORTING_COPY.errorTitle, message: error };
  // BOTH LEDGERS, before anything is drawn. Unlike the Transaction Centre —
  // where the fees are the subject and two counts may arrive a beat later —
  // every figure here is a comparison of the two, and a total drawn from one of
  // them would be a cost with no income beside it, which is the one way this
  // screen could mislead.
  if (!fees || !settlements) {
    return { kind: "loading", message: REPORTING_COPY.loading };
  }

  const period = resolvePeriod(input.periodKey, now, input.custom);
  const all = [...feeRows(fees, workBase), ...shareRows(settlements, workBase)];

  const currencies = positionsByCurrency(all).map((each) => ({
    currency: each.currency,
    label: REPORTING_COPY.currency.choice(each.currency, each.records),
    records: each.records,
  }));
  const selected = currency && currencies.some((each) => each.currency === currency)
    ? currency
    : (currencies[0]?.currency ?? null);

  // A half-typed custom range reports its problem and plots nothing rather than
  // falling back to a period the reader did not ask for.
  const inPeriod =
    period.problem !== null
      ? []
      : all.filter(
          (row) => row.currency === selected && withinPeriod(row.dateIso, period),
        );

  const counted = inPeriod.filter((row) => row.status !== "void");
  const voided = inPeriod.filter((row) => row.status === "void");
  const position = selected
    ? (positionsByCurrency(inPeriod)[0] ?? emptyPosition(selected))
    : null;
  const grouping = isMonthly(period, counted)
    ? REPORTING_COPY.charts.byMonth
    : REPORTING_COPY.charts.byDay;

  return {
    kind: "ready",
    period,
    currencies,
    currency: selected,
    totals: position ? describePosition(position) : [],
    netLine: position ? describeNet(position) : "",
    comparison: position ? describeComparison(position) : [],
    comparisonLabel: REPORTING_COPY.charts.comparisonLabel(selected ?? ""),
    buckets: bucketsFor(counted, period, selected ?? ""),
    timeLabel: REPORTING_COPY.charts.timeLabel(selected ?? "", grouping),
    grouping,
    rows: sortRows(counted, sort),
    voidRows: sortRows(voided, sort),
    emptyNote: emptyNoteFor(inPeriod.length, currencies.length, period),
    position,
  };
}

function emptyNoteFor(
  inPeriod: number,
  currencies: number,
  period: ResolvedPeriod,
): string | null {
  if (period.problem !== null) return period.problem;
  if (inPeriod > 0) return null;
  // Nothing anywhere is a different sentence from nothing in March, and a
  // gallery that has never sold should not be told to widen its period.
  return currencies === 0
    ? REPORTING_COPY.table.emptyAllTime
    : REPORTING_COPY.table.empty;
}

function isMonthly(period: ResolvedPeriod, rows: ReportingRow[]): boolean {
  if (period.startIso && period.endIso) {
    return Date.parse(period.endIso) - Date.parse(period.startIso) > DAILY_SPAN_DAYS * DAY_MS;
  }
  const stamps = rows.map((row) => Date.parse(row.dateIso)).filter((at) => !Number.isNaN(at));
  if (stamps.length === 0) return true;

  return Math.max(...stamps) - Math.min(...stamps) > DAILY_SPAN_DAYS * DAY_MS;
}

// ────────────────────────────────────────────────────────── the export ──

/**
 * The report as a CSV, built from the SAME rows and totals the screen drew.
 *
 * IT RUNS IN THE BROWSER, and that is a departure from OND-67 worth arguing
 * rather than assuming. `collector-list-export.ts` moved the collector list's
 * export to the server on the grounds that "a file the browser assembles is a
 * file no server can refuse, bound or write down". Two things make this file
 * different, and both have to hold:
 *
 *   * WHAT IT DISCLOSES IS THE FIRM'S OWN MONEY. The columns are a work, a
 *     date, a status, an amount and a currency — no counterparty, no reseller,
 *     no buyer, no address, no email. The collector list is a list of PEOPLE
 *     and the audit row is the product's answer to who read it; there is no
 *     third party here whose record is being handed over, so there is nothing
 *     for `ondo.access_events` to be about.
 *   * A SERVER COPY WOULD BE A SECOND LEDGER. OND-97 asks that the export
 *     "apply the same calculation logic as the screen", and the only way to
 *     mean that literally is to call the same function. An export built in SQL
 *     or in a service would be a second implementation of the period
 *     boundaries, the five statuses and the four rules above — and CLAUDE.md's
 *     standing warning is that two copies of a rule drift, with the symptom
 *     here being a spreadsheet that disagrees with the screen it came from.
 *
 * What DOES carry over from OND-67 is carried over rather than re-derived: the
 * cell rule is `@ondo/shared`'s `csvCell`, lifted out of that file so both
 * exports neutralise a leading `=` the same way, and the per-currency "never a
 * total" discipline is the whole shape of this one.
 *
 * THE AMOUNT COLUMN IS A NUMBER AND THE CURRENCY IS ITS OWN COLUMN. A cell
 * reading '£172,000' is text a spreadsheet cannot sum and a reader cannot
 * filter; and the moment two currencies sit in one numeric column somebody sums
 * it, which is why a file only ever covers the one currency named at its head.
 */
export function toReportingCsv(input: {
  period: ResolvedPeriod;
  currency: string;
  position: CurrencyPosition;
  rows: ReportingRow[];
  voidRows: ReportingRow[];
  exportedAt: Date;
}): string {
  const { period, currency, position, rows, voidRows, exportedAt } = input;
  const headings = REPORTING_COPY.csvHeadings;
  const all = [...rows, ...voidRows];

  const major = (minor: number) => (minor / 100).toFixed(2);

  return toCsv([
    [headings.document],
    // The preset and the dates in two cells rather than one sentence: a reader
    // filtering a pile of these files wants the label, and a reader checking
    // one wants the dates.
    [headings.period, period.label, period.rangeLabel],
    [headings.periodFrom, period.startIso ?? ""],
    [headings.periodTo, period.endIso ?? ""],
    [headings.currency, currency],
    [headings.basis, REPORTING_COPY.basis],
    // Named rather than implied: a reader has to know that voided rows are in
    // the file and in none of the totals above them.
    [headings.statuses, describeStatuses(position)],
    [headings.exportedAt, exportedAt.toISOString()],
    [headings.records, String(all.length)],
    [headings.feesDue, major(position.feesDueMinor), currency],
    [headings.feesPaid, major(position.feesPaidMinor), currency],
    [headings.sharesDue, major(position.sharesDueMinor), currency],
    [headings.sharesReceived, major(position.sharesReceivedMinor), currency],
    [headings.note, REPORTING_COPY.csvNote],
    [],
    [
      REPORTING_COPY.table.columns.side,
      REPORTING_COPY.table.columns.record,
      REPORTING_COPY.table.columns.work,
      "ONDO code",
      "Artist",
      REPORTING_COPY.table.columns.date,
      REPORTING_COPY.table.columns.status,
      REPORTING_COPY.table.columns.amount,
      REPORTING_COPY.table.columns.currency,
    ],
    ...all.map((row) => [
      row.sideLabel,
      row.kindLabel,
      row.title,
      row.ondoCode,
      row.attributedName,
      // The INSTANT, not the rendered day: a spreadsheet can read an ISO date
      // and cannot sort '14 Mar 2026'.
      row.dateIso,
      row.statusLabel,
      // A row with no figure is EMPTY rather than 0. A zero in a numeric column
      // is a figure somebody will sum, and this one does not exist yet.
      row.amountMinor === null ? "" : major(row.amountMinor),
      row.currency,
    ]),
  ]);
}

function describeStatuses(position: CurrencyPosition): string {
  const parts: string[] = [REPORTING_COPY.status.due, REPORTING_COPY.status.recorded];
  if (position.sharesPending > 0) parts.push(REPORTING_COPY.status.pending);
  if (position.sharesNothingOwed > 0) parts.push(REPORTING_COPY.status.nothing_owed);
  if (position.feesVoided + position.sharesVoided > 0) {
    parts.push(`${REPORTING_COPY.status.void} (listed, counted in no total)`);
  }

  return parts.join(" · ");
}

/** What the file is called, and it names the scope so two downloads never collide. */
export function reportingFilename(period: ResolvedPeriod, currency: string): string {
  if (!period.startIso || !period.endIso) {
    return REPORTING_COPY.export.filenameAllTime(currency);
  }

  return REPORTING_COPY.export.filename(
    currency,
    period.startIso.slice(0, 10),
    new Date(Date.parse(period.endIso) - 1).toISOString().slice(0, 10),
  );
}

/**
 * What went wrong, in words a gallery member can act on.
 *
 * A 403 on these routes is not a mistyped id — neither takes one. It means the
 * caller holds no gallery membership at all, which is what every other id-less
 * route on this rail says in the same words.
 */
export function describeReportingError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 403) return REPORTING_COPY.errors.refused;
    if (cause.message) return cause.message;
  }

  return REPORTING_COPY.errorTitle;
}
